<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import AcoesPasso from '../components/AcoesPasso.vue';
import ConfirmarPular from '../components/ConfirmarPular.vue';
import MetaCard from '../components/MetaCard.vue';
import WhatsappQrCard from '../components/WhatsappQrCard.vue';
import { whatsappDone } from '../visorSteps';
import { BTN, BTN_LINK, BTN_SECONDARY, BTN_SMALL, CARD, CHIP, CHIP_TONES, ENTER, H1, SMALL, SUB, TILE, TILE_TONES } from '../ui';

const props = defineProps({
  state: { type: Object, required: true },
  accountId: { type: [Number, String], required: true },
});
const emit = defineEmits(['next', 'save', 'skip']);

const { t } = useI18n();
const confirming = ref(false);
const whatsappCard = ref(null);

const realEstate = computed(() => Boolean(props.state.real_estate));
const done = computed(() => whatsappDone(props.state));
const accountName = computed(() => props.state.facts?.name?.value || '');
const phone = computed(() => props.state.facts?.whatsapp?.value || '');
const catalog = computed(() => props.state.catalog || {});

const advance = () => {
  if (done.value || props.state.progress?.whatsapp_skipped) {
    emit('next');
    return;
  }
  confirming.value = true;
};

const connectNow = () => {
  confirming.value = false;
  whatsappCard.value?.$el?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
};

const skip = () => {
  confirming.value = false;
  emit('skip');
};
</script>

<template>
  <div>
    <section :class="ENTER">
      <h1 :class="H1">
        <span class="text-n-slate-10">{{ t('ONBOARDING_VISOR.ACCOUNTS.HELLO') }}</span>
        <br />
        {{ t('ONBOARDING_VISOR.ACCOUNTS.TITLE') }}
      </h1>
      <p :class="SUB">
        {{ realEstate ? t('ONBOARDING_VISOR.ACCOUNTS.SUBTITLE_REAL_ESTATE') : t('ONBOARDING_VISOR.ACCOUNTS.SUBTITLE') }}
      </p>
    </section>

    <div class="grid gap-6 mt-8 lg:grid-cols-[1.5fr_1fr] items-start max-md:gap-4 max-md:mt-5">
      <WhatsappQrCard
        ref="whatsappCard"
        :account-id="accountId"
        :done="done"
        :pending-inbox-id="state.whatsapp?.inbox_id || null"
        :inbox-name="t('ONBOARDING_VISOR.ACCOUNTS.WHATSAPP.INBOX_NAME', { name: accountName })"
        :phone="phone"
        @inbox-created="id => emit('save', { whatsapp_inbox_id: id })"
        @connected="emit('save', { whatsapp_connected: true })"
      />
      <MetaCard />

      <section v-if="realEstate" :class="CARD" class="lg:col-span-2" data-testid="catalogo-card">
        <div class="flex items-center gap-3 mb-4 flex-wrap">
          <span :class="[TILE, TILE_TONES.ok]"><Icon icon="i-ph-buildings-duotone" /></span>
          <h2 class="text-xl font-medium tracking-[-0.02em]">
            {{ t('ONBOARDING_VISOR.ACCOUNTS.CATALOG.TITLE') }}
          </h2>
          <span :class="[CHIP, catalog.configured ? CHIP_TONES.ok : CHIP_TONES.aviso]">
            <Icon :icon="catalog.configured ? 'i-ph-check-circle-duotone' : 'i-ph-warning-duotone'" class="size-3.5" />
            {{ catalog.configured ? t('ONBOARDING_VISOR.ACCOUNTS.CATALOG.LINKED') : t('ONBOARDING_VISOR.ACCOUNTS.CATALOG.MISSING') }}
          </span>
        </div>
        <div class="flex items-center gap-4 justify-between max-md:flex-col max-md:items-start">
          <div>
            <b class="font-medium">{{ catalog.url || t('ONBOARDING_VISOR.ACCOUNTS.CATALOG.NO_URL') }}</b>
            <p :class="SMALL">
              {{ catalog.configured ? t('ONBOARDING_VISOR.ACCOUNTS.CATALOG.LINKED_HINT') : t('ONBOARDING_VISOR.ACCOUNTS.CATALOG.MISSING_HINT') }}
            </p>
          </div>
          <router-link
            :to="{ name: 'ai_settings', params: { accountId, category: 'comentarios', section: 'catalogo' } }"
            :class="catalog.configured ? BTN_LINK : [BTN_SECONDARY, BTN_SMALL]"
          >
            {{ catalog.configured ? t('ONBOARDING_VISOR.ACCOUNTS.CATALOG.CHANGE') : t('ONBOARDING_VISOR.ACCOUNTS.CATALOG.CONNECT') }}
          </router-link>
        </div>
      </section>
    </div>

    <AcoesPasso :trust="t('ONBOARDING_VISOR.ACCOUNTS.TRUST')">
      <button type="button" :class="BTN" data-testid="seguir" @click="advance">
        {{ realEstate ? t('ONBOARDING_VISOR.ACCOUNTS.NEXT_PROPERTIES') : t('ONBOARDING_VISOR.ACCOUNTS.NEXT_PANEL') }}
        <Icon icon="i-ph-arrow-right-duotone" class="size-5" />
      </button>
      <button v-if="!done" type="button" :class="BTN_LINK" data-testid="pular" @click="advance">
        {{ t('ONBOARDING_VISOR.ACCOUNTS.SKIP') }}
      </button>
    </AcoesPasso>

    <ConfirmarPular
      v-if="confirming"
      :real-estate="realEstate"
      @connect="connectNow"
      @skip="skip"
    />
  </div>
</template>
